import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { EmptyState } from "@/components/app/lists/empty-state";
import { OffsetPaginationFooter } from "@/components/app/lists/offset-pagination-footer";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { optionalInt, optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { usePolicy } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { formatSeconds } from "../-components/conversation-state";
import { ConversationRow } from "@/routes/_authenticated/_shelby/shelby/conversations/-components/conversation-row";

const STATE_PARAMS = ["live", "closed"] as const;
type ConversationsSearch = {
  state?: (typeof STATE_PARAMS)[number];
  days?: 7 | 90;
  offset?: number;
  size?: number;
};

export const Route = createFileRoute("/_authenticated/_shelby/shelby/conversations/")({
  validateSearch: (search: Record<string, unknown>): ConversationsSearch => ({
    state: optionalOneOf(STATE_PARAMS, search.state),
    days: [7, 90].includes(optionalInt(search.days, 7, 90) ?? 0)
      ? (optionalInt(search.days, 7, 90) as 7 | 90)
      : undefined,
    offset: optionalInt(search.offset, 1, 1_000_000),
    size: optionalInt(search.size, 5, 200),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "shelby-conversations"),
  component: SalesConversations,
});

const WINDOWS = [7, 30, 90] as const;
const STATES = [
  { value: "all", label: "All" },
  { value: "live", label: "Live now" },
  { value: "closed", label: "Written up" },
] as const;

/** Every sales assistant conversation, with tiles for answered, leads, tours and how long a family
 * waited for a person, which shows whether "human first" is true. */
function SalesConversations() {
  const { facilityId } = useFacility();
  const canSetUp = usePolicy().navigation.some((entry) => entry.id === "shelby-settings");
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<ConversationsSearch>) =>
    void navigate({
      search: (prev) => ({ ...prev, offset: undefined, ...next }),
      ...KEEP_PLACE,
    });
  const state: (typeof STATES)[number]["value"] = params.state ?? "all";
  const setState = (next: (typeof STATES)[number]["value"]) =>
    patch({ state: next === "all" ? undefined : next });
  const days = params.days ?? 30;
  const setDays = (next: number) => patch({ days: next === 7 ? 7 : next === 90 ? 90 : undefined });
  const offset = params.offset ?? 0;
  const setOffset = (next: number) =>
    void navigate({
      search: (prev) => ({ ...prev, offset: next > 0 ? next : undefined }),
      ...KEEP_PLACE,
    });
  const limit = params.size ?? 25;
  const setLimit = (next: number) => patch({ size: next === 25 ? undefined : next });

  const list = useQuery({
    ...orpc.shelby.conversations.queryOptions({
      input: { facilityId, state, days, limit, offset },
    }),
    // A live conversation changes hands while somebody is looking at the list.
    refetchInterval: 15_000,
  });
  const data = list.data;
  const stats = data?.stats;
  const shown = data?.rows.length ?? 0;
  // The pick-up figure is said in words too ("under half"), so it is not colour alone.
  const under = Boolean(
    stats && stats.conversations > 0 && stats.personPickedUp < stats.conversations / 2,
  );

  return (
    <>
      <PageHeader
        title="Sales conversations"
        count={data?.total}
        meta={`What the sales assistant has answered in the last ${days} days${facilityId === "all" ? " across your communities" : ""}, and who picked each one up.`}
      />

      {/* Six zeros above an empty list say nothing the empty state does not. */}
      {stats && stats.conversations === 0 ? null : (
        <StatTiles>
          <StatTile
            label="Conversations"
            value={stats?.conversations ?? "-"}
            hint={`last ${days} days`}
          />
          <StatTile
            label="From the website"
            value={stats?.fromWebsite ?? "-"}
            hint={`of ${stats?.conversations ?? 0}`}
          />
          <StatTile
            label="Leads opened"
            value={stats?.leadsOpened ?? "-"}
            hint={`of ${stats?.conversations ?? 0}`}
          />
          <StatTile
            label="Tours or calls asked"
            value={stats?.toursRequested ?? "-"}
            hint={`of ${stats?.conversations ?? 0}`}
          />
          <StatTile
            label="A person picked up"
            value={stats?.personPickedUp ?? "-"}
            hint={`of ${stats?.conversations ?? 0}${under ? ", under half" : ""}`}
            tone={under ? "warn" : undefined}
          />
          <StatTile
            label="Median time to a person"
            value={
              stats?.medianPickupSeconds === null || stats?.medianPickupSeconds === undefined
                ? "-"
                : formatSeconds(stats.medianPickupSeconds)
            }
            hint="from alert to first reply"
          />
        </StatTiles>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap items-center gap-2">
          {STATES.map((option) => (
            <FilterChip
              key={option.value}
              selected={state === option.value}
              onClick={() => {
                setState(option.value);
              }}
            >
              {option.label}
            </FilterChip>
          ))}
        </div>
        {/* The window is what the tiles count over, not another filter on the
            list, so it is separated rather than sitting in the same row of
            chips as the three states. */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-sm">Over the last</span>
          {WINDOWS.map((window) => (
            <FilterChip
              key={window}
              selected={days === window}
              onClick={() => {
                setDays(window);
              }}
            >
              {window} days
            </FilterChip>
          ))}
        </div>
      </div>

      {list.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : list.isError ? (
        <ReadFailed
          message="Couldn't load the conversations."
          error={list.error}
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      ) : shown === 0 && (state !== "all" || days < 90) ? (
        <Card>
          <CardContent>
            <EmptyState
              title={`No ${state === "live" ? "live " : state === "closed" ? "written-up " : ""}conversations in the last ${days} days`}
              description={
                state === "all"
                  ? "Older conversations may still have some."
                  : "The other states may still have some."
              }
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    patch({ state: undefined, days: 90 });
                  }}
                >
                  Show all from the last 90 days
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : shown === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              title="No conversations yet"
              description="When a family texts from the website, or the front desk passes an admissions inquiry, it appears here with who holds it and the lead it opened."
              action={
                canSetUp ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/shelby/settings">Set up the sales assistant</Link>
                  </Button>
                ) : undefined
              }
            />
          </CardContent>
        </Card>
      ) : (
        <Card className="gap-0 overflow-hidden py-0">
          <ol className="divide-y">
            {data?.rows.map((row) => (
              <ConversationRow key={row.id} row={row} />
            ))}
          </ol>
          {data && data.total > 0 ? (
            <OffsetPaginationFooter
              offset={offset}
              pageSize={limit}
              total={data.total}
              noun="conversations"
              onOffsetChange={setOffset}
              onPageSizeChange={setLimit}
            />
          ) : null}
        </Card>
      )}
    </>
  );
}
